import React from 'react';
import { EyeIcon, CheckCircleIcon, AlertTriangleIcon, MergeIcon, UndoIcon, XIcon } from 'lucide-react';
import { CustomerRecord } from './DuplicateCard';
type MultiFieldSelection = {
  [fieldName: string]: string | null;
};
type MultiMergePreviewProps = {
  records: CustomerRecord[];
  fieldSelections: MultiFieldSelection;
  primaryRecordId: string | null;
  onMerge: () => void;
  onCancel: () => void;
  isVisible: boolean;
  mergeStatus: 'idle' | 'merging' | 'success' | 'error';
  onUndo?: () => void;
};
export function MultiMergePreview({
  records,
  fieldSelections,
  primaryRecordId,
  onMerge,
  onCancel,
  isVisible,
  mergeStatus,
  onUndo
}: MultiMergePreviewProps) {
  if (!isVisible) return null;
  const getMergedValue = (fieldName: keyof CustomerRecord): string => {
    const selectedRecordId = fieldSelections[fieldName];
    if (selectedRecordId) {
      const record = records.find(r => r.id === selectedRecordId);
      return record ? String(record[fieldName]) : '';
    }
    const defaultRecord = primaryRecordId ? records.find(r => r.id === primaryRecordId) : records[0];
    return defaultRecord ? String(defaultRecord[fieldName]) : '';
  };
  const unresolvedConflicts = Object.entries(fieldSelections).filter(([fieldName, selectedRecordId]) => {
    const values = records.map(r => String(r[fieldName as keyof CustomerRecord]));
    const uniqueValues = new Set(values);
    return uniqueValues.size > 1 && selectedRecordId === null;
  }).length;
  const formatCurrency = (amount: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(Number(amount));
  };
  const primaryRecord = primaryRecordId ? records.find(r => r.id === primaryRecordId) : records[0];
  if (mergeStatus === 'success') {
    return <div className="fixed bottom-0 left-0 right-0 bg-green-50 border-t-2 border-green-500 p-6 shadow-2xl z-20" data-id="element-223">
        <div className="max-w-5xl mx-auto flex items-center justify-between" data-id="element-224">
          <div className="flex items-center gap-4" data-id="element-225">
            <div className="flex-shrink-0 w-12 h-12 bg-green-100 rounded-full flex items-center justify-center" data-id="element-226">
              <CheckCircleIcon className="w-6 h-6 text-green-600" aria-hidden="true" data-id="element-227" />
            </div>
            <div data-id="element-228">
              <h3 className="text-base font-semibold text-green-900" data-id="element-229">
                Records Merged Successfully!
              </h3>
              <p className="text-sm text-green-700" data-id="element-230">
                {records.length} duplicate records have been combined into a
                single record.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3" data-id="element-231">
            {onUndo && <button onClick={onUndo} className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-green-300 text-green-700 rounded-lg hover:bg-green-50 transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 font-medium text-sm" data-id="element-232">
                <UndoIcon className="w-4 h-4" aria-hidden="true" data-id="element-233" />
                Undo Merge
              </button>}
            <button onClick={onCancel} className="p-2 text-green-700 hover:text-green-900 hover:bg-green-100 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-green-500" aria-label="Close success message" data-id="element-234">
              <XIcon className="w-5 h-5" aria-hidden="true" data-id="element-235" />
            </button>
          </div>
        </div>
      </div>;
  }
  return <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-blue-500 shadow-2xl z-20" data-id="element-236">
      {/* Header */}
      <div className="bg-blue-50 px-6 py-4 flex items-center justify-between border-b border-blue-100" data-id="element-237">
        <div className="flex items-center gap-3" data-id="element-238">
          <EyeIcon className="w-5 h-5 text-blue-600" aria-hidden="true" data-id="element-239" />
          <h3 className="text-base font-semibold text-blue-900" data-id="element-240">
            Merge Preview
          </h3>
          {unresolvedConflicts > 0 && <span className="flex items-center gap-1.5 text-sm text-yellow-800 bg-yellow-100 px-3 py-1 rounded-full border border-yellow-200 font-medium" data-id="element-241">
              <AlertTriangleIcon className="w-4 h-4" aria-hidden="true" data-id="element-242" />
              {unresolvedConflicts} unresolved
            </span>}
        </div>
        <button onClick={onCancel} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500" aria-label="Close preview" data-id="element-243">
          <XIcon className="w-5 h-5" aria-hidden="true" data-id="element-244" />
        </button>
      </div>

      {/* Preview Content */}
      <div className="p-6" data-id="element-245">
        <div className="max-w-5xl mx-auto" data-id="element-246">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-id="element-247">
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-248">
              <p className="text-xs font-medium text-gray-500 mb-1.5" data-id="element-249">Name</p>
              <p className="text-sm font-semibold text-gray-900 truncate" data-id="element-250">
                {getMergedValue('firstName')} {getMergedValue('lastName')}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-251">
              <p className="text-xs font-medium text-gray-500 mb-1.5" data-id="element-252">Email</p>
              <p className="text-sm font-semibold text-gray-900 truncate" data-id="element-253">
                {getMergedValue('email')}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-254">
              <p className="text-xs font-medium text-gray-500 mb-1.5" data-id="element-255">Phone</p>
              <p className="text-sm font-semibold text-gray-900" data-id="element-256">
                {getMergedValue('phone')}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-257">
              <p className="text-xs font-medium text-gray-500 mb-1.5" data-id="element-258">
                Loan Amount
              </p>
              <p className="text-sm font-semibold text-gray-900" data-id="element-259">
                {formatCurrency(getMergedValue('loanAmount'))}
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-200" data-id="element-260">
            <p className="text-sm text-gray-600" data-id="element-261">
              This will merge {records.length} records into{' '}
              <span className="font-semibold text-gray-900" data-id="element-262">
                {primaryRecord?.id || 'primary record'}
              </span>
              . Other records will be archived.
            </p>
            <div className="flex items-center gap-3" data-id="element-263">
              <button onClick={onCancel} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400" data-id="element-264">
                Cancel
              </button>
              <button onClick={onMerge} disabled={unresolvedConflicts > 0 || mergeStatus === 'merging'} className={`flex items-center gap-2 px-6 py-2 rounded-lg text-sm font-semibold transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${unresolvedConflicts > 0 ? 'bg-gray-200 text-gray-400 cursor-not-allowed border-2 border-gray-300' : 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm hover:shadow-md'}`} aria-disabled={unresolvedConflicts > 0} data-id="element-265">
                {mergeStatus === 'merging' ? <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" aria-hidden="true" data-id="element-266" />
                    Merging...
                  </> : <>
                    <MergeIcon className="w-4 h-4" aria-hidden="true" data-id="element-267" />
                    Merge {records.length} Records
                  </>}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>;
}